// src/components/AnalysisDashboard.js
import React, { useEffect, useState } from "react";
import "../css/AnalysisDashboard.css";
import questions from "../data/questions.json";
import { saveAs } from "file-saver";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(BarElement, CategoryScale, LinearScale);

// Gemini API config
const geminiApiKey = process.env.REACT_APP_GEMINI_API_KEY;
const geminiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${geminiApiKey}`;

const AnalysisDashboard = () => {
  const [categoryCounts, setCategoryCounts] = useState({});
  const [trendingTitles, setTrendingTitles] = useState({});
  const [aiAdvice, setAiAdvice] = useState("");

  useEffect(() => {
    const categoryCount = {};
    const titleCount = {};

    questions.forEach((q) => {
      categoryCount[q.category] = (categoryCount[q.category] || 0) + 1;
      titleCount[q.title] = (titleCount[q.title] || 0) + 1;
    });

    const sortedTrending = Object.entries(titleCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .reduce((obj, [k, v]) => ({ ...obj, [k]: v }), {});

    setCategoryCounts(categoryCount);
    setTrendingTitles(sortedTrending);
  }, []);

  useEffect(() => {
    if (Object.keys(trendingTitles).length === 0) return;

    const prompt = `Give 3 short, clear teaching tips to help students understand these common STEM questions: ${Object.keys(trendingTitles).join(", ")}`;

    fetch(geminiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contents: [{ parts: [{ text: prompt }] }],
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const aiText =
          data?.candidates?.[0]?.content?.parts?.[0]?.text ||
          "AI teaching suggestions unavailable.";
        setAiAdvice(aiText);
      })
      .catch(() =>
        setAiAdvice("⚠️ Unable to connect to Gemini AI.")
      );
  }, [trendingTitles]);

  const exportToCSV = () => {
    let csvContent = "Category,Count\n";
    Object.entries(categoryCounts).forEach(([key, value]) => {
      csvContent += `${key},${value}\n`;
    });

    csvContent += "\nTrending Topics,Count\n";
    Object.entries(trendingTitles).forEach(([key, value]) => {
      csvContent += `${key},${value}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "analysis-report.csv");
  };

  const chartData = {
    labels: Object.keys(categoryCounts),
    datasets: [
      {
        label: "Number of Questions",
        data: Object.values(categoryCounts),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
        borderColor: "rgba(59, 130, 246, 1)",
        borderWidth: 1,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: { 
      legend: { 
        display: false 
      } 
    },
    scales: { 
      y: { 
        beginAtZero: true, 
        ticks: { 
          stepSize: 1,
          color: 'rgba(255, 255, 255, 0.7)'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      x: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      }
    },
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <h1>STEM Questions Analysis</h1>

        <section>
          <h2>📚 Category Breakdown</h2>
          <ul>
            {Object.entries(categoryCounts).map(([cat, count]) => (
              <li key={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)} —{" "}
                <strong>{count}</strong> questions
              </li>
            ))}
          </ul>
          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </section>

        <section>
          <h2>🔥 Trending Topics (This Month)</h2>
          <ul>
            {Object.entries(trendingTitles).map(([title, count]) => (
              <li key={title}>
                {title} — <strong>{count}</strong> times asked
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2>💡 AI Teaching Suggestions</h2>
          <p className="ai-response">{aiAdvice}</p>
        </section>

        <button className="export-button" onClick={exportToCSV}>
          📥 Download Report (CSV)
        </button>
      </div>
    </div>
  );
};

export default AnalysisDashboard;